/**
 * StackedAreaChart (Phase 3, §6.3/§6.4) — SPINE. Cumulative filled areas for the
 * per-bucket spend trend. 2px surface gap between adjacent stacked fills,
 * selective direct labels on the ≤4 largest series, de-DE crosshair tooltip, and
 * a legend + table mirror. Values are spend magnitudes, but single months can
 * go negative (refund-only month) — the stacking GEOMETRY clamps those cells to
 * 0 so layers never self-intersect or spill below the axis, while the tooltip
 * and table keep showing the honest signed values.
 */
import { useState } from 'react';
import { money, moneyShort } from '../../../lib/format.ts';
import { Axis, niceTicks, scaleLinear, type AxisTick } from './Axis.tsx';
import { ChartFrame } from './ChartFrame.tsx';
import { Legend, type LegendItem } from './Legend.tsx';
import { Tooltip, type TooltipRow } from './Tooltip.tsx';

export interface AreaSeries {
  key: string;
  label: string;
  color: string;
  values: number[];
}

export interface StackedAreaChartProps {
  title: string;
  subtitle?: string;
  description?: string;
  categories: string[];
  series: AreaSeries[];
  formatValue?: (n: number) => string;
  formatTick?: (n: number) => string;
  aspect?: number;
}

const M = { left: 56, right: 20, top: 16, bottom: 28 };
const DIRECT_LABEL_COUNT = 4;

export function StackedAreaChart({
  title,
  subtitle,
  description,
  categories,
  series,
  formatValue = money,
  formatTick = moneyShort,
  aspect = 0.5,
}: StackedAreaChartProps) {
  const [hover, setHover] = useState<number | null>(null);
  const n = categories.length;

  const legendItems: LegendItem[] = series.map((s) => ({
    label: s.label,
    color: s.color,
    shape: 'square' as const,
  }));

  const columns = ['Zeitraum', ...series.map((s) => s.label), 'Summe'];
  const rows: (string | number)[][] = categories.map((cat, i) => {
    const total = series.reduce((acc, s) => acc + (s.values[i] ?? 0), 0);
    return [cat, ...series.map((s) => formatValue(s.values[i] ?? 0)), formatValue(total)];
  });

  const totals = series.map((s) => s.values.reduce((a, v) => a + (v ?? 0), 0));
  const labelled = new Set(
    [...totals.keys()].sort((a, b) => totals[b] - totals[a]).slice(0, DIRECT_LABEL_COUNT),
  );

  const scrollMinWidth = n > 8 ? n * 56 + M.left + M.right : undefined;

  return (
    <ChartFrame
      title={title}
      subtitle={subtitle}
      description={description}
      aspect={aspect}
      scrollMinWidth={scrollMinWidth}
      legend={<Legend items={legendItems} />}
      table={{ columns, rows, caption: title }}
    >
      {({ width, height }) => {
        const plotLeft = M.left;
        const plotRight = width - M.right;
        const plotTop = M.top;
        const plotBottom = height - M.bottom;
        const innerW = Math.max(0, plotRight - plotLeft);
        const innerH = Math.max(0, plotBottom - plotTop);

        // Clamp negative cells (refund months) to 0 for the stack geometry —
        // raw signed values stay visible in the tooltip + table.
        const stackTotals = categories.map((_, i) =>
          series.reduce((acc, s) => acc + Math.max(0, s.values[i] ?? 0), 0),
        );
        const scale = niceTicks(0, Math.max(1, ...stackTotals), 5);
        const yScale = scaleLinear(scale.min, scale.max, plotBottom, plotTop);
        const cx = (i: number) =>
          n > 1 ? plotLeft + (innerW * i) / (n - 1) : plotLeft + innerW / 2;

        const yTicks: AxisTick[] = scale.ticks.map((v) => ({
          pos: yScale(v),
          label: formatTick(v),
        }));
        const xTicks: AxisTick[] = categories.map((label, i) => ({ pos: cx(i), label }));

        // Cumulative stack, bottom → top.
        const bottoms = categories.map(() => 0);
        const layers = series.map((s) => {
          const tops = s.values.map((v, i) => bottoms[i] + Math.max(0, v ?? 0));
          const topPts = tops.map((t, i) => `${cx(i)},${yScale(t)}`);
          const bottomPts: string[] = [];
          for (let i = n - 1; i >= 0; i -= 1) bottomPts.push(`${cx(i)},${yScale(bottoms[i])}`);
          const area = [...topPts, ...bottomPts].join(' ');
          const topLine = topPts.join(' ');
          const labelIdx = n - 1;
          const labelMidValue = (bottoms[labelIdx] + tops[labelIdx]) / 2;
          for (let i = 0; i < n; i += 1) bottoms[i] = tops[i];
          return { series: s, area, topLine, labelY: yScale(labelMidValue) };
        });

        const bandW = n > 0 ? innerW / n : innerW;

        return (
          <>
            <Axis
              left={plotLeft}
              top={plotTop}
              innerWidth={innerW}
              innerHeight={innerH}
              yTicks={yTicks}
              xTicks={xTicks}
            />

            {layers.map(({ series: s, area, topLine, labelY }, li) => (
              <g key={s.key}>
                <polygon points={area} fill={s.color} opacity={0.85} />
                {/* 2px surface gap between adjacent stacked fills */}
                <polyline
                  points={topLine}
                  fill="none"
                  stroke="var(--viz-surface)"
                  strokeWidth={2}
                  strokeLinejoin="round"
                />
                {labelled.has(li) && n > 0 ? (
                  <text
                    x={cx(n - 1) - 4}
                    y={labelY}
                    textAnchor="end"
                    dominantBaseline="middle"
                    fontSize={11}
                    fontWeight={600}
                    fill="var(--viz-surface)"
                    style={{ paintOrder: 'stroke' }}
                    stroke={s.color}
                    strokeWidth={3}
                  >
                    {s.label}
                  </text>
                ) : null}
              </g>
            ))}

            <g onPointerLeave={() => setHover(null)}>
              {categories.map((_, i) => (
                <rect
                  key={`hit-${i}`}
                  x={plotLeft + bandW * i}
                  y={plotTop}
                  width={bandW}
                  height={innerH}
                  fill="transparent"
                  onPointerEnter={() => setHover(i)}
                />
              ))}
              {hover !== null ? (
                <line
                  x1={cx(hover)}
                  x2={cx(hover)}
                  y1={plotTop}
                  y2={plotBottom}
                  stroke="var(--viz-axis)"
                  strokeWidth={1}
                  strokeDasharray="3 3"
                  pointerEvents="none"
                />
              ) : null}
            </g>

            {hover !== null ? (
              <Tooltip
                x={cx(hover)}
                y={plotTop}
                title={categories[hover]}
                chartWidth={width}
                rows={series.map<TooltipRow>((s) => ({
                  label: s.label,
                  value: formatValue(s.values[hover] ?? 0),
                  color: s.color,
                }))}
              />
            ) : null}
          </>
        );
      }}
    </ChartFrame>
  );
}
